import type { ReactNode } from 'react';
import { useSession } from '../session';
import { SignOutButton } from './SignOutButton';
import { Bell } from './Bell';
import { ConnectionBanner } from '../ui/ConnectionBanner';
import { UnsentWorkPanel } from '../ui/UnsentWorkPanel';
import { WriteRefusalNotice } from '../ui/WriteRefusalNotice';

/**
 * The tech surface shell — purpose-built mobile (PRD v2 §1/§5): same tokens,
 * radically fewer elements, thumb-first.
 *
 * The header names the PERSON, not the page. It used to say "Today", which stopped being
 * true the moment there was a second route: a header that claims one screen while you're
 * standing on another is exactly the small lie that makes an app feel untrustworthy. Every
 * page states itself in its own heading.
 */
export function TechShell({ children }: { children: ReactNode }) {
  const session = useSession();
  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col">
      <header className="flex items-center justify-between border-b border-border bg-surface px-4 py-3">
        <div className="min-w-0">
          <p className="truncate font-heading text-lg font-bold leading-tight">
            {session.name ?? session.email}
          </p>
          {session.name ? (
            <p className="truncate text-xs text-muted-foreground">{session.email}</p>
          ) : null}
        </div>
        {/* The bell in the header that is already here (ticket 05): no sticky bar added. A
            tech's notifications open on the visit of the ticket's job. */}
        <div className="flex shrink-0 items-center gap-1">
          <Bell className="size-11" />
          <SignOutButton iconSize={18} />
        </div>
      </header>
      {/* Directly under the header, above every tech screen: losing signal is the one
          piece of context that changes what a tech should expect from every tap. */}
      <ConnectionBanner />
      {/* Under the banner, above every tech screen for the same reason — except that this one
          needs a THUMB, not just an eye. Work the server refused belongs nowhere in
          particular, so it belongs everywhere: the stop that made it is three jobs back by
          the time the queue replays. See UnsentWorkPanel. */}
      <UnsentWorkPanel />
      {/* A change this device would not send (a full disk, the same change twice). */}
      <WriteRefusalNotice />
      <main className="min-h-0 flex-1">{children}</main>
    </div>
  );
}
